import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Shield, Mail, Key, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface OTPResetPasswordFormProps {
  email?: string;
  onBack?: () => void;
}

export const OTPResetPasswordForm: React.FC<OTPResetPasswordFormProps> = ({ 
  email: initialEmail = "", 
  onBack 
}) => {
  const [step, setStep] = useState<'email' | 'verify'>('email');
  const [email, setEmail] = useState(initialEmail);
  const [otpCode, setOtpCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: t("auth.otpReset.toast.error"),
        description: t("auth.otpReset.toast.enterEmail"),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-password-reset-otp', {
        body: { email }
      });

      if (error) {
        throw error;
      }

      setOtpSent(true);
      setStep('verify');
      toast({
        title: t("auth.otpReset.toast.otpSent"),
        description: t("auth.otpReset.toast.checkEmail"),
      });

    } catch (error: any) {
      console.error('Error sending OTP:', error);
      toast({
        title: t("auth.otpReset.toast.error"),
        description: error.message || t("auth.otpReset.toast.sendFailed"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otpCode || otpCode.length !== 6) {
      toast({
        title: t("auth.otpReset.toast.invalidOtp"),
        description: t("auth.otpReset.toast.enterCode"),
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: t("auth.otpReset.toast.passwordMismatch"),
        description: t("auth.otpReset.toast.passwordsDoNotMatch"),
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: t("auth.otpReset.toast.passwordTooShort"),
        description: t("auth.otpReset.toast.passwordMinLength"),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('verify-password-reset-otp', {
        body: { 
          email,
          otp_code: otpCode,
          new_password: password
        }
      });

      if (error) {
        console.error('Edge function error:', error);
      }

      if (!data?.success) {
        toast({
          title: t("auth.otpReset.toast.verificationFailed"),
          description: data?.error || t("auth.otpReset.toast.invalidOrExpired"),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: t("auth.otpReset.toast.passwordUpdated"),
        description: t("auth.otpReset.toast.passwordUpdatedDesc"),
      });

      navigate('/login');

    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      toast({
        title: t("auth.otpReset.toast.verificationFailed"),
        description: error.message || t("auth.otpReset.toast.invalidOrExpired"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    await handleSendOTP({ preventDefault: () => {} } as React.FormEvent);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-trust p-4">
      <Card className="w-full max-w-md shadow-elegant border-border/50">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-3 bg-primary/10 rounded-full">
              {step === 'email' ? (
                <Mail className="h-8 w-8 text-primary" />
              ) : (
                <Shield className="h-8 w-8 text-primary" />
              )}
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-foreground">
              {step === 'email' ? t("auth.otpReset.titleEmail") : t("auth.otpReset.titleVerify")}
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {step === 'email'
                ? t("auth.otpReset.descriptionEmail")
                : t("auth.otpReset.descriptionVerify")
              }
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {onBack && (
            <Button
              variant="ghost"
              onClick={onBack}
              className="w-full mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("auth.otpReset.backToResetOptions")}
            </Button>
          )}

          {step === 'email' ? (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.otpReset.emailLabel")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("auth.otpReset.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="transition-all duration-300 focus:shadow-medical"
                />
              </div>

              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  {t("auth.otpReset.alertDescription")}
                </AlertDescription>
              </Alert>

              <Button
                type="submit"
                variant="hero"
                size="lg"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? t("auth.otpReset.sendingCode") : t("auth.otpReset.sendCode")}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otpCode">{t("auth.otpReset.otpCodeLabel")}</Label>
                <div className="relative">
                  <Input
                    id="otpCode"
                    type="text"
                    placeholder="000000"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    required
                    className="text-center text-2xl font-mono tracking-widest transition-all duration-300 focus:shadow-medical"
                  />
                  <Key className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  {t("auth.otpReset.codeSentTo", { email })}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t("auth.otpReset.newPasswordLabel")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("auth.otpReset.newPasswordPlaceholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10 transition-all duration-300 focus:shadow-medical"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("auth.otpReset.confirmPasswordLabel")}</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t("auth.otpReset.confirmPasswordPlaceholder")}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="pr-10 transition-all duration-300 focus:shadow-medical"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                variant="hero"
                size="lg"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? t("auth.otpReset.updatingPassword") : t("auth.otpReset.updatePassword")}
              </Button>

              <div className="text-center">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleResendOTP}
                  disabled={isLoading}
                  className="text-sm"
                >
                  {t("auth.otpReset.resendCode")}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};